import { Form, Formik } from 'formik';
import { ValuesPwd } from './UserProfileForm.types';
import FormField from '../../components/field/FormField';
import { useTranslation } from '../../context/translationContext';
import Button from '../../components/button/Button';
import http from '../../api/axios';
import { apiUrl } from '../../api/apiUrl';
import { toast } from 'react-toastify';
import { handleApiErrors } from '../../api/apiErrorHandler';
import * as Yup from 'yup';
import { useLoading } from '../../context/loadingContext';

/**
 * Form to edit current user´s password
 */
export const UserProfileChangePasswordForm = () => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const _initialValues: ValuesPwd = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  /**
   * Changes password for current user
   */
  const handleSubmit = async (values: ValuesPwd) => {
    try {
      startLoading();
      const response = await http.apiPost({
        url: apiUrl.CHANGE_PASSWORD,
        data: { oldPassword: values.oldPassword, newPassword: values.newPassword },
      });
      if (response?.status === 204) {
        stopLoading();
        toast.success('Success!');
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Rules for password
   * old password - required
   * new password - required, min 6 chars, max 100 chars, must contain special char
   * confirm password - required, must match new password
   */
  const ChangePasswordSchema = Yup.object().shape({
    oldPassword: Yup.string().required(t('page.user-setting.old-pwd-required')),
    newPassword: Yup.string()
      .required(t('page.user-setting.pwd-required'))
      .min(6, t('page.user-setting.validation-pwd-min-lenght'))
      .max(100, t('page.user-setting.validation-pwd-max-lenght'))
      .matches(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[\d])(?=.*?[^\w\s]).{6,100}$/,
        t('page.user-setting.validation-pwd-special-char'),
      ),
    confirmPassword: Yup.string()
      .required(t('page.user-setting.pwd-confirm-required'))
      .when('newPassword', {
        is: (value: string) => value && value.length > 0,
        then: Yup.string().oneOf([Yup.ref('newPassword')], t('page.user-setting.pwd-do-not-match')),
      })
      .required(t('page.user-setting.pwd-confirm-required')),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={_initialValues}
      onSubmit={handleSubmit}
      validationSchema={ChangePasswordSchema}
    >
      {({ errors, touched }) => (
        <Form>
          <div className="my-4">
            <FormField
              id="oldPassword"
              name="oldPassword"
              type="password"
              label={t('page.user-setting.input-old-pwd')}
              errors={errors}
              touched={touched}
            />
          </div>
          <div className="my-4">
            <FormField
              id="newPassword"
              name="newPassword"
              type="password"
              label={t('page.user-setting.input-new-pwd')}
              errors={errors}
              touched={touched}
            />
          </div>
          <div className="my-4">
            <FormField
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              label={t('page.user-setting.input-confirm-pwd')}
              errors={errors}
              touched={touched}
            />
          </div>
          <Button title={t('page.user-setting.btn-change-password')} type="submit" color="green" />
        </Form>
      )}
    </Formik>
  );
};
